import { ArrowRight, ChevronDown, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { allFaqs, faqGroups } from '../data/faqs';
import { Seo } from '../lib/seo';

export function FaqPage() {
  const [open, setOpen] = useState<string[]>([faqGroups[0].items[0].question]);
  const [query, setQuery] = useState('');

  const groups = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return faqGroups;
    return faqGroups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => `${item.question} ${item.answer}`.toLowerCase().includes(term))
      }))
      .filter((group) => group.items.length > 0);
  }, [query]);

  const toggle = (question: string) =>
    setOpen((current) =>
      current.includes(question) ? current.filter((value) => value !== question) : [...current, question]
    );

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: allFaqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: { '@type': 'Answer', text: faq.answer }
    }))
  };

  return (
    <section className="page-section faq-page">
      <Seo
        title="Nexhance AI FAQ - 120+ Answers on AI Image Tools, Free Utilities & Credits"
        description="Search 120+ answers covering AI image enhancement, upscaling, restoration, object and text removal, backgrounds, generative expand, free image utilities, formats, credits, privacy and troubleshooting."
        path="/faq"
        jsonLd={faqSchema}
      />
      <div className="shell">
        <div className="page-hero compact-page-hero">
          <p className="eyebrow">120+ practical answers</p>
          <h1>Clear answers for AI edits, free image tools and account questions.</h1>
          <p>Learn which of the 25 tools fits the job, when a free browser utility is enough, what AI can realistically reconstruct, how credits work and what to check before downloading a result.</p>
        </div>

        <section className="faq-popular-paths" aria-labelledby="faq-popular-heading">
          <div><p className="eyebrow">Popular help paths</p><h2 id="faq-popular-heading">Start with the image problem or account question.</h2></div>
          <div className="faq-popular-grid">
            <Link to="/guides/how-to-make-a-picture-clearer"><strong>Make a picture clearer</strong><span>Enhance, deblur, denoise or sharpen?</span></Link>
            <Link to="/guides/increase-image-resolution-without-losing-quality"><strong>Increase image resolution</strong><span>Choose 2×, 4× or 8× safely.</span></Link>
            <Link to="/guides/scan-old-photos-for-ai-restoration"><strong>Restore an old photo</strong><span>Prepare a better scan first.</span></Link>
            <Link to="/guides/transparent-png-background-guide"><strong>Create a transparent PNG</strong><span>Preserve difficult edges and shadows.</span></Link>
            <Link to="/tools/object-remover"><strong>Remove an object or text</strong><span>Choose cleanup, text removal or a crop.</span></Link>
            <Link to="/tools/image-extender"><strong>Extend an image</strong><span>Use generative expand without cropping the subject.</span></Link>
            <Link to="/tools/compress"><strong>Prepare images for the web</strong><span>Compress, resize or convert with 0 credits.</span></Link>
            <Link to="/plans-and-credits"><strong>Understand credits</strong><span>Free, monthly and annual allowances.</span></Link>
            <Link to="/contact"><strong>Contact support</strong><span>Billing, account or technical assistance.</span></Link>
          </div>
        </section>

        <label className="faq-search">
          <Search size={19} />
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search enhancement, object removal, WebP, social sizes, privacy, credits…" />
        </label>

        <div className="faq-groups">
          {groups.map((group) => (
            <section className="faq-group" key={group.title}>
              <div className="faq-group-heading">
                <p className="eyebrow">{group.title}</p>
                <p>{group.description}</p>
              </div>
              <div className="faq-list">
                {group.items.map((faq) => {
                  const isOpen = open.includes(faq.question);
                  const id = `faq-${faq.question.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
                  return (
                    <div key={faq.question} className={`faq-item ${isOpen ? 'open' : ''}`}>
                      <button
                        className="faq-q"
                        onClick={() => toggle(faq.question)}
                        aria-expanded={isOpen}
                        aria-controls={id}
                      >
                        {faq.question}
                        <ChevronDown size={20} />
                      </button>
                      <div className="faq-a" id={id} role="region">
                        <div><p>{faq.answer}</p></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        {groups.length === 0 && (
          <div className="empty-faq-search">
            <h2>No matching question</h2>
            <p>Try a shorter term such as credits, privacy, upscale, object, background, WebP, social, metadata or refund.</p>
          </div>
        )}

        <div className="guide-cta faq-cta">
          <div>
            <p className="eyebrow">Ready to test a workflow?</p>
            <h2>Choose the simplest tool that solves the image problem, then review the result before downloading.</h2>
          </div>
          <div className="faq-cta-actions"><Link className="button button-primary button-large" to="/tools">Explore AI tools <ArrowRight size={18} /></Link><Link className="button button-ghost button-large" to="/contact">Contact support</Link></div>
        </div>
      </div>
    </section>
  );
}
